import { DatePicker, Form, message, Modal, Spin, Typography } from 'antd';
import { Company, LicenseEntitlement, LicenseType } from '../../../../types';
import { dateFormats, licenseEntitlements } from '../../../../constants';
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  convertLicense,
  createTrialLicense,
  extendLicense,
} from '../../../../utils/httpRequest';

const CreateLicenseModal = ({
  open,
  company,
  onConfirm,
  onClose,
  entitlement = LicenseEntitlement.CORE,
  limitEndDate,
  limitStartDate,
  isExtend,
  licenseType = LicenseType.TRIAL,
  action = 'create',
  licenseKey = '',
}: {
  open: boolean;
  company: Company;
  onConfirm: () => void;
  onClose: () => void;
  entitlement?: LicenseEntitlement;
  licenseKey?: string;
  limitEndDate?: string;
  limitStartDate?: string;
  isExtend?: boolean;
  licenseType?: LicenseType;
  action?: 'create' | 'extend' | 'convert';
}) => {
  const [form] = Form.useForm();
  const datesRef = useRef<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const entitlementName =
    licenseEntitlements.find((i) => i.id === entitlement)?.title || entitlement;

  const isTrial = licenseType === LicenseType.TRIAL;

  const handleOk = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const dates = datesRef.current;
      const payload = {
        companyID: company.companyID,
        entitlement,
        startDate: dates[0],
        endDate: dates[1],
        type: licenseType,
      };
      switch (action) {
        case 'create':
          await createTrialLicense(payload);
          break;
        case 'convert':
          await convertLicense(licenseKey, payload);
          break;
        case 'extend':
          await extendLicense(licenseKey, payload);
          break;

        default:
          break;
      }
      onConfirm();
    } catch (err: any) {
      if (!err?.errorFields) {
        messageApi.error(err || 'License create failed!');
      }
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRangeChange = (
    dates: null | (Dayjs | null)[],
    dateStrings: string[]
  ) => {
    if (dates) {
      datesRef.current = dateStrings;
    } else {
      console.log('Clear');
      datesRef.current = [];
    }
  };

  const setDefaultDates = () => {
    const startDate = limitStartDate
      ? dayjs(limitStartDate)
      : dayjs().startOf('day');
    const endDate = limitEndDate
      ? dayjs(limitEndDate)
      : dayjs(startDate).startOf('day').add(1, 'year').subtract(1, 'day');
    form.setFieldsValue({
      dates: [startDate, endDate],
    });
    datesRef.current = [
      startDate.format(dateFormats.date),
      endDate.format(dateFormats.date),
    ];
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    if (open && licenseType === LicenseType.PRODUCTION) {
      setDefaultDates();
    }
  }, [open]);

  const getTitle = () => {
    switch (action) {
      case 'create':
        return `Create ${entitlementName} - ${isTrial ? 'Trail' : ''} License for ${company?.name ? `"${company?.name}"` : 'Client'}`;
      case 'extend':
        return `Extend ${entitlementName} License`;
      case 'convert':
        return `Convert ${entitlementName} License to production`;
      default:
        return '';
    }
  };

  return (
    <Modal
      open={open}
      title={getTitle()}
      onCancel={onClose}
      onClose={onClose}
      onOk={handleOk}
      okText={action === 'create' ? 'Create' : 'Confirm'}
      confirmLoading={loading}
    >
      {contextHolder}
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Form.Item>
            <Typography.Text>
              Please select license period to continue.
            </Typography.Text>
          </Form.Item>
          <Form.Item
            label="License Period"
            name="dates"
            rules={[
              {
                required: true,
                message: 'Please select Start Date!',
              },
            ]}
          >
            <DatePicker.RangePicker
              format={dateFormats.date}
              onChange={handleRangeChange}
              disabledDate={(current) =>
                current <
                  (limitStartDate
                    ? dayjs(limitStartDate)
                    : dayjs().startOf('day')) ||
                current > dayjs(limitEndDate || '2099-01-01')
              }
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateLicenseModal;
